import React from "react";
import ParticleField from "../ParticleField";
import { Palette, PALETTES } from "../holidayConfig";
import { Grain, H, Layer, Mist, mix, ridge, snowCap, Stars, W } from "./kit";
import { Attendant, Look, Lucia, LuciaDefs, PaperStar, StarBoy } from "./LuciaCharacters";
import { Sparkle, ToonDefs } from "./toon";
import Enkehus from "./Enkehus";
import { GustafVasaDome, LIT, skyline, SNOW, SNOW_SHADE, SnowyTree, StreetLamp, Tenement, VasastanDefs, WARM } from "./Vasastan";
import { range, seeded } from "./sceneUtils";
import "./scenes.css";

/*
 * Lucia: blå timme en decembermorgon på Norrtullsgatan i Vasastan.
 * Till höger Borgerskapets änkehus (kontoret) med snö på taket och
 * adventsljus i fönstren. Längst bort stenstadens tak och Gustaf Vasa
 * kyrkas kupol vid Odenplan, närmare snötyngda lindar, Stockholmslyktor
 * och sekelskifteshus med adventsstjärnor och ljusstakar i fönstren.
 * Luciatåget går i snön till vänster, på väg mot änkehuset, och
 * parkens smidesstaket syns i förgrunden till höger.
 */

const SKY_TOP = "#03051a";
const SKY_MID = "#0c1240";
const NIGHT = "#141a4a";

/** Änkehusets port. Fasaden slutar före skärmkanten och hjulet. */
const ENKEHUS = { x: 1398, y: 490, s: 0.95 };
/** Gatunivå i mellanlagret. */
const STREET = 492;

/** Parkens smidesstaket med ringar mellan räckena och snö på överliggaren. */
const ParkFence: React.FC<{ from: number; to: number; y: number }> = ({ from, to, y }) => {
  const iron = "#070818";
  const posts = range(Math.floor((to - from) / 8) + 1).map(i => from + i * 8);
  return (
    <g>
      <g fill={iron} stroke={iron}>
        <path d={`M${from} ${y - 26} H${to} M${from} ${y - 6} H${to}`} strokeWidth="2.2" fill="none" />
        {posts.map(px => (
          <path key={px} d={`M${px - 1} ${y + 6} V${y - 30} L${px} ${y - 34} L${px + 1} ${y - 30} V${y + 6} Z`} stroke="none" />
        ))}
        {posts.slice(0, -1).map(px => (
          <circle key={`r${px}`} cx={px + 4} cy={y - 16} r="3.4" fill="none" strokeWidth="1" />
        ))}
      </g>
      <path d={snowCap(from - 2, to + 2, y - 27.6, 2, 71)} fill={SNOW} />
    </g>
  );
};

interface HangSpec {
  left: string;
  width: string;
  thread: number;
  duration: number;
}

const STARS: HangSpec[] = [
  { left: "32%", width: "clamp(40px, 4.2vw, 74px)", thread: 20, duration: 6 },
  { left: "37.5%", width: "clamp(28px, 2.8vw, 50px)", thread: 90, duration: 5 },
  { left: "62%", width: "clamp(30px, 3vw, 54px)", thread: 60, duration: 5.6 },
  { left: "67%", width: "clamp(44px, 4.6vw, 80px)", thread: 14, duration: 6.4 },
];

/** Liten skära av månen. */
const Crescent: React.FC<{ p: Palette }> = ({ p }) => (
  <div className="hol-orb" style={{ left: "89%", top: "22%", width: "clamp(110px, 11vw, 190px)" }}>
    <svg viewBox="-100 -100 200 200">
      <defs>
        <radialGradient id="lu-moonhalo" cx="50%" cy="50%" r="50%">
          <stop offset="15%" stopColor={p[200]} stopOpacity="0.35" />
          <stop offset="100%" stopColor={p[400]} stopOpacity="0" />
        </radialGradient>
        <mask id="lu-crescent">
          <circle r="34" fill="#ffffff" />
          <circle cx="16" cy="-10" r="30" fill="#000000" />
        </mask>
      </defs>
      <circle r="100" fill="url(#lu-moonhalo)" />
      <circle r="34" fill="#f5f6ff" mask="url(#lu-crescent)" />
    </svg>
  </div>
);

/*
 * Luciatåget i par bakom Lucia. De flesta är ljushyade; en mörkhyad tärna
 * och en tärna med östasiatiskt utseende. Bara två sjunger med öppen mun.
 */
const LUCIA: Look = { skin: "fair", hair: "blonde", style: "long", eyes: "closed", mouth: "o" };
const TARNA_PUFFS: Look = { skin: "deep", hair: "afro", style: "puffs", eyes: "down", mouth: "smile", iris: "#3a2418", blink: -3.2 };
const TARNA_BANGS: Look = { skin: "golden", hair: "black", style: "bangs", eyes: "open", mouth: "sing", iris: "#2a1c16", blink: -4.6 };
const TARNA_BRAIDS: Look = { skin: "light", hair: "red", style: "braids", eyes: "open", mouth: "smile", iris: "#4f7a4a", freckles: true, blink: -1 };
const BOY_SHORT: Look = { skin: "fair", hair: "brown", style: "short", eyes: "open", mouth: "grin", iris: "#4a7a5a", freckles: true, blink: -2.2 };
const BOY_CURLY: Look = { skin: "light", hair: "dark", style: "curly", eyes: "open", mouth: "smile", iris: "#4a3426", blink: -5.4 };

const LuciaScene: React.FC<{ p: Palette; dark: boolean }> = ({ p, dark }) => {
  const sky = skyline(23, -40, W + 40, 452, [700, 900]);
  const rNear = ridge({ seed: 127, base: 572, amp: 22, scale: 3, shape: x => (x < 480 ? 6 : x > 1140 ? -4 : 0) });
  const amber = PALETTES.amber;
  const farBody = mix(p[900], SKY_MID, 0.5);
  const farSnow = mix(SNOW, SKY_MID, 0.5);
  const treeFar = mix("#0a0c22", SKY_MID, 0.25);
  const glints = range(18).map(i => {
    const rnd = seeded(500 + i);
    const gx = rnd() * W;
    return { x: gx, y: rNear.y(gx) + 6 + rnd() * 20, r: 1.4 + rnd() * 1.8, d: -rnd() * 3 };
  });

  return (
    <>
      <ToonDefs />
      <LuciaDefs />
      <VasastanDefs />
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="lu-street" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={mix(SNOW, NIGHT, 0.32)} />
            <stop offset="100%" stopColor={mix(SNOW_SHADE, NIGHT, 0.4)} />
          </linearGradient>
          <linearGradient id="lu-snow-near" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f6f7ff" />
            <stop offset="45%" stopColor={mix("#ffffff", p[200], 0.5)} />
            <stop offset="100%" stopColor={mix(p[200], p[500], 0.45)} />
          </linearGradient>
        </defs>
      </svg>
      {/* Blå timme: ljusare himmel nere vid taken */}
      <div
        className="hol-sky-fill"
        style={{
          background: `radial-gradient(ellipse 80% 40% at 50% 100%, ${mix(p[500], SKY_MID, 0.4)} 0%, transparent 75%), linear-gradient(180deg, ${SKY_TOP} 0%, ${SKY_MID} 36%, ${mix(p[900], SKY_MID, 0.3)} 64%, ${mix(p[700], SKY_MID, 0.4)} 100%)`,
        }}
      />
      <Stars seed={57} count={240} color="#eef0ff" maxY={430} />
      <div className="hol-shooting-star" style={{ left: "70%", top: "8%", animationDuration: "14s" }} />
      <Crescent p={p} />

      {/* Stenstadens tak och Gustaf Vasa kyrka vid Odenplan */}
      <Layer delay={0}>
        <GustafVasaDome x={800} y={392} night={SKY_MID} glow={amber[400]} />
        <path d={sky.body} fill={farBody} />
        <path d={sky.roofs} fill={farSnow} />
        <path d={sky.lit} fill={LIT} opacity="0.7" />
      </Layer>
      <Mist bottom="22vh" height="14vh" color={`${p[300]}16`} duration={70} />

      {/* Norrtullsgatan */}
      <Layer delay={140}>
        <path d={`M-40 ${STREET - 2} H${W + 40} V${H} H-40 Z`} fill="url(#lu-street)" />
        {/* Hjulspår i snön */}
        <path d={`M-40 ${STREET + 22} H${W + 40} M-40 ${STREET + 30} H${W + 40}`} stroke={mix(SNOW_SHADE, NIGHT, 0.5)} strokeWidth="2.4" opacity="0.5" />
        {/* Parkens lindar bakom hjulet */}
        <SnowyTree x={590} y={STREET + 4} len={92} width={11} seed={88} color={treeFar} />
        <SnowyTree x={1020} y={STREET + 4} len={86} width={10} seed={93} color={treeFar} />
        <SnowyTree x={1218} y={STREET + 4} len={100} width={12} seed={97} color={mix("#080a1e", NIGHT, 0.2)} />
        {/* Hyreshusen till vänster */}
        <Tenement spec={{ x: -30, w: 168, h: 214, wall: "#d8b067", cols: 5, roof: "mansard", seed: 3 }} base={STREET} night={NIGHT} />
        <Tenement spec={{ x: 138, w: 150, h: 228, wall: "#e3d2a0", cols: 4, roof: "gable", seed: 11 }} base={STREET} night={NIGHT} />
        <Tenement spec={{ x: 288, w: 178, h: 206, wall: "#c99a86", cols: 5, roof: "turret", seed: 19 }} base={STREET} night={NIGHT} />
        {/* Grannhuset till höger om änkehuset */}
        <Tenement spec={{ x: 1552, w: 130, h: 232, wall: "#e2c98a", cols: 4, roof: "mansard", seed: 29 }} base={STREET} night={NIGHT} />
        <Enkehus x={ENKEHUS.x} y={ENKEHUS.y} s={ENKEHUS.s} p={amber} mood="lucia" />
        <StreetLamp x={482} y={STREET + 8} />
        <StreetLamp x={1200} y={STREET + 8} delay={-1.8} />
        {/* Snövall längs trottoaren */}
        <path d={`${snowCap(-40, 1245, STREET + 6, 5, 61)}`} fill={mix(SNOW, NIGHT, 0.18)} />
      </Layer>
      <Mist bottom="4vh" height="10vh" color={`${p[100]}14`} duration={50} reverse />

      {/* Snön i förgrunden med luciatåget och parkens staket */}
      <Layer delay={280}>
        <path d={rNear.d} fill="url(#lu-snow-near)" />
        {glints.map((g, i) => (
          <Sparkle key={i} x={g.x} y={g.y} r={g.r} color="#ffffff" className="hol-twinkle" delay={g.d} />
        ))}
        <ellipse cx="230" cy="586" rx="260" ry="32" fill="url(#lu-glow)" />
        <ParkFence from={1150} to={W + 30} y={H - 4} />
        {/* Andra raden (längre bort) */}
        <Attendant x={292} y={574} s={0.78} p={p} look={TARNA_BANGS} delay={-1.8} />
        <StarBoy x={170} y={572} s={0.76} p={p} look={BOY_CURLY} delay={-0.9} />
        <Attendant x={50} y={572} s={0.74} p={p} look={TARNA_BRAIDS} delay={-2.6} />
        {/* Främre raden och Lucia först */}
        <Lucia x={360} y={590} s={0.98} p={p} look={LUCIA} />
        <Attendant x={236} y={594} s={0.9} p={p} look={TARNA_PUFFS} delay={-1.2} />
        <StarBoy x={110} y={594} s={0.9} p={p} look={BOY_SHORT} delay={-0.4} />
      </Layer>

      <ParticleField kind="snow" dark={dark} density={0.7} />
      {STARS.map((h, i) => (
        <div key={i} className="hol-hang hol-hide-md" style={{ left: h.left, width: h.width, animationDelay: `${300 + parseFloat(h.left) * 12}ms` }}>
          <div className="hol-hang-swing" style={{ animationDuration: `${h.duration}s`, animationDelay: `${-h.duration * 0.4}s` }}>
            <PaperStar thread={h.thread} />
          </div>
        </div>
      ))}
      <Grain opacity={0.06} />
      {/* Varmt skimmer där tåget går och vid änkehusets port */}
      <div className="hol-sky-fill" style={{ pointerEvents: "none", background: `radial-gradient(ellipse 30% 35% at 14% 92%, ${WARM}1c, transparent 70%), radial-gradient(ellipse 18% 22% at 87% 88%, ${WARM}12, transparent 70%)` }} />
    </>
  );
};

export default LuciaScene;
